import { COMMANDS, commandOnSurface, shortcuts, isAvailableElement } from "./commands.js"

const submitSelector = 'button[type="submit"], input[type="submit"], button:not([type])'

function sidebarNavigation(element) {
  return COMMANDS[element.dataset.command]?.group === "navigation" && element.closest("#sidebar_nav_container")
}

function navigationKey(element) {
  // Only ordinary links can be equivalent across header/footer controls.
  // Custom handlers, submissions and downloads must keep their own identity.
  if (!element.matches('a[href]') || !/^https?:/.test(element.href) ||
    element.matches('[data-action], [onclick], [data-turbo-method], [data-method], [data-remote], [download], [data-bs-toggle]')) return undefined
  const frame = element.closest("turbo-frame")
  const inherited = ["data-turbo", "data-turbo-frame", "data-turbo-action"]
    .map(attribute => element.closest(`[${attribute}]`)?.getAttribute(attribute))
  return JSON.stringify([
    element.href, element.target, element.getAttribute("rel"), frame?.id, frame?.getAttribute("target"), ...inherited,
    ...["data-turbo-confirm", "data-confirm", "data-turbo-stream"].map(attribute => element.getAttribute(attribute))
  ])
}

export function labelFor(element) {
  if (element.dataset.commandLabel || element.getAttribute("aria-label") || element.value) {
    return element.dataset.commandLabel || element.getAttribute("aria-label") || element.value
  }
  const label = element.cloneNode(true)
  label.querySelectorAll(".keyboard-shortcut-hint").forEach(hint => hint.remove())
  return label.textContent.trim()
}

export function collectDomCommands(root, { enabled = true } = {}) {
  const commands = [...root.querySelectorAll("[data-command]")].map(element => ({
    id: element.dataset.command,
    label: labelFor(element),
    element,
    navigationKey: navigationKey(element),
    // Authorized sidebar destinations remain usable when their section is collapsed.
    visibilityElement: sidebarNavigation(element) ? root : undefined,
    enabled: sidebarNavigation(element) ? () => !element.matches('.disabled, [aria-disabled="true"]') &&
      !element.closest('[data-shortcuts="off"], [inert], [aria-busy="true"]') : undefined,
    form: element.form || element.closest("form") ||
      element.closest('[data-controller~="remote-modal"]')?.querySelector('[data-remote-modal-target~="form"]'),
    action: () => {
      if (element.matches('input[type="search"]')) { element.focus(); element.select() }
      else element.click()
    }
  }))

  // Page-level Rails record actions are already authorized and retain their URLs.
  // Row menus and tabs require explicit opt-in to avoid acting on another record.
  for (const element of root.querySelectorAll('#page_title a.btn[href]:not([data-command]):not([data-turbo-method])')) {
    const path = new URL(element.href, root.ownerDocument.baseURI).pathname
    const back = element.querySelector(".fa-arrow-left, .bi-arrow-left") || /^Back(?:\s|$)/i.test(labelFor(element))
    const id = back ? "record.back" : path.endsWith("/new") ? "record.create" : path.endsWith("/edit") ? "record.edit" : null
    if (id) commands.push({ id, label: labelFor(element), element, navigationKey: navigationKey(element), action: () => element.click() })
  }

  // Simple Form record forms share save behavior across every Rails engine.
  // Other forms opt in explicitly; GET filters and destructive forms are excluded.
  for (const form of root.querySelectorAll('#page_body form.simple_form, dialog form.simple_form, form[data-shortcut-save="true"]')) {
    if (form.querySelector('[data-command="record.save"]') ||
      form.closest('dialog')?.querySelector('[data-command="record.save"]') ||
      form.closest('[data-shortcut-scope="widget"]')) continue
    const method = form.querySelector('input[name="_method"]')?.value || form.method
    if (!["post", "patch", "put"].includes(method?.toLowerCase())) continue
    const submitters = [...form.querySelectorAll(submitSelector)].filter(element => element.getClientRects().length > 0)
    if (submitters.length !== 1) continue
    const element = submitters[0]
    if (element.hasAttribute("data-command")) continue
    commands.push({ id: "record.save", label: labelFor(element), element, action: () => element.click() })
  }

  // Use real, authorized sidebar links and their organization-scoped URLs.
  const navigationPaths = new Set(commands.filter(command => command.element.matches('a[href]') && isAvailableElement(command.element)).map(command => command.element.getAttribute("href")))
  const navigationSelector = '#sidebar_wrapper a[href], header nav a[href], nav[aria-label*="navigation" i] a[href]'
  for (const element of root.querySelectorAll(navigationSelector)) {
    if (sidebarNavigation(element)) continue
    if (element.hasAttribute("data-turbo-method")) continue
    if (element.getAttribute("href").startsWith("#") || !element.textContent.trim()) continue
    if (navigationPaths.has(element.getAttribute("href"))) continue
    navigationPaths.add(element.getAttribute("href"))
    commands.push({
      id: element.dataset.command || `navigate:${element.getAttribute("href")}`, label: labelFor(element), element,
      navigationKey: navigationKey(element),
      // Collapsed/mobile sidebars still expose their rendered, authorized routes.
      visibilityElement: root,
      enabled: () => !element.matches('.disabled, [aria-disabled="true"]') && !element.closest('[data-shortcuts="off"]'),
      action: () => element.click()
    })
  }
  return commands.filter(command => commandOnSurface(command.id, root.dataset.keyboardShortcutsSurface)).map(command => {
    const definition = { ...COMMANDS[command.id], keys: enabled ? shortcuts.bindingFor(command.id) : undefined }
    return { ...definition, ...command }
  })
}
